import { useState } from "react";
import { sendAuthenticatedRequest } from "../common/functions";
import SignupStepIndicator from "../common/components/SignupStepIndicator";
import { useUser } from "../common/stateDefs";

/**
 * Onboarding step after the company details: share the shop's purchase
 * history (a CSV / Excel / PDF table of what it bought — material, spec,
 * size, quantity, weight, date). The rows become the shop's demand history,
 * which is what lets us forecast its needs and pre-position inventory. The
 * step can be skipped; My information offers it again later.
 */
export default function PurchaseHistoryStep() {
  const user = useUser((state) => state.user);
  const setUser = useUser((state) => state.setUser);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<{
    lines: number;
    dropped: number;
    columns: Record<string, string>;
  } | null>(null);
  const [done, setDone] = useState<{ rows: number } | null>(null);

  const api = process.env.REACT_APP_BF_API_BASE_URL;

  const readFile = (f: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        typeof reader.result === "string"
          ? resolve(reader.result)
          : reject(new Error("read failed"));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(f);
    });

  const markDone = (patch: NonNullable<typeof user>["purchaseHistory"]) => {
    if (user)
      setUser({
        ...user,
        purchaseHistory: { ...(user.purchaseHistory ?? {}), ...patch },
      });
  };

  const onPick = async (f: File | null) => {
    setFile(f);
    setPreview(null);
    setError("");
    if (!f) return;
    if (f.size > 10_000_000) {
      setError("Please keep the file under 10MB.");
      return;
    }
    setBusy(true);
    try {
      const data = await readFile(f);
      const resp = await sendAuthenticatedRequest(`${api}/purchase-history`, {
        method: "POST",
        jsonBody: { data, filename: f.name, preview: true },
      });
      if (!resp.ok) throw new Error(await resp.text());
      setPreview(await resp.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "We could not read that file.");
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const data = await readFile(file);
      const resp = await sendAuthenticatedRequest(`${api}/purchase-history`, {
        method: "POST",
        jsonBody: { data, filename: file.name, note },
      });
      if (!resp.ok) throw new Error(await resp.text());
      const result = (await resp.json()) as {
        purchaseRecords: number;
        rfqEvents: number;
        submissionId: string;
      };
      const rows = result.purchaseRecords + result.rfqEvents;
      setDone({ rows });
      markDone({
        uploadedAt: new Date().toISOString(),
        submissionId: result.submissionId,
        rows,
        fileName: file.name,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    try {
      await sendAuthenticatedRequest(`${api}/purchase-history/skip`, {
        method: "POST",
        jsonBody: {},
      });
    } catch {
      // Still let them in; the profile page offers the step again.
    }
    markDone({ skippedAt: new Date().toISOString() });
  };

  const recognized = preview ? Object.keys(preview.columns) : [];

  return (
    <div className="mx-auto md:w-[34em] px-4 py-10">
      <SignupStepIndicator currentStep={2} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <p className="text-lg font-semibold">
          Your account is ready. One optional step:
        </p>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-600">
          Optional
        </span>
      </div>
      <p className="mb-4 text-sm text-gray-700">
        You can go straight to the platform now. If you would like, share a
        table of what your shop has bought (material or grade, spec, size,
        quantity, weight and date) as CSV, Excel or a PDF table. We use it to
        anticipate what you will need and keep it in stock near you. It is never
        shared with other customers or vendors, and you can do it later from My
        information.
      </p>
      {!done && (
        <button
          type="button"
          className="mb-5 w-full rounded-full border-2 border-[#00062e] bg-[#00062e] px-6 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50 sm:w-auto"
          onClick={skip}
          disabled={busy}
        >
          Skip for now and go to the platform
        </button>
      )}

      {done ? (
        <div className="rounded border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800">
          Thank you — {done.rows} purchase lines were recorded. Taking you to
          the platform…
        </div>
      ) : (
        <>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Or share your purchase history (optional)
          </p>
          <input
            type="file"
            accept=".csv,.xlsx,.pdf"
            className="mb-3 block w-full text-sm"
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
          {preview && (
            <div className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              {preview.lines} rows with a material recognized
              {preview.dropped > 0 &&
                `, ${preview.dropped} skipped (no material)`}
              .
              {recognized.length > 0 && (
                <> Columns understood: {recognized.join(", ")}.</>
              )}
              {!recognized.includes("date") && (
                <span className="block text-amber-700">
                  No date column was found — purchase dates make the history far
                  more useful. Add one if you can.
                </span>
              )}
            </div>
          )}
          <textarea
            className="mb-3 h-16 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="Anything we should know about this history (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
          {error && (
            <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="rounded-full border-2 border-[#00062e] px-6 py-2 text-sm font-bold text-[#00062e] hover:bg-slate-100 disabled:opacity-50"
              onClick={upload}
              disabled={busy || !file || !preview || preview.lines === 0}
            >
              {busy ? "Working..." : "Share purchase history"}
            </button>
            <button
              type="button"
              className="text-sm text-gray-600 underline hover:text-gray-900"
              onClick={skip}
              disabled={busy}
            >
              Skip for now
            </button>
          </div>
        </>
      )}
    </div>
  );
}
